/**
 * useContextMenu.ts - Context menu state management hook
 *
 * Manages state for the canvas context menu including:
 * - Menu visibility and position
 * - Target node information
 * - Branch data for switch nodes
 *
 * Related:
 *   - components/CanvasContextMenu.tsx - Renders the menu UI
 *   - components/AutomationFlowCanvas.tsx - Integrates the context menu
 */

import { useCallback, useState } from 'react';
import type { Node } from '@xyflow/react';
import type * as Api from '@ViewModels';
import type { BaseNodeData } from '../types';
import type { SegmentNodeData } from '../types/stepNodes';
import { isAutomationStepNodeData } from '../types/typeGuards';

/**
 * Branch information extracted from a switch node for the context menu
 */
export interface BranchInfo {
	/** Zero-based index of the branch in the switch cases array */
	caseIndex: number;
	/** Human-readable name of the branch condition */
	caseName: string;
	/** Whether this is the default fallback branch */
	isDefault: boolean;
	/** Color associated with this branch */
	caseColor?: string;
}

/**
 * Context menu state including position and target information
 */
export interface ContextMenuState {
	/** Whether the menu is currently visible */
	isOpen: boolean;
	/** Screen coordinates for positioning the menu */
	position: { x: number; y: number };
	/** ID of the node that was right-clicked */
	targetNodeId: string | null;
	/** Type of the target node (e.g., 'switchStep', 'emailStep', 'segment') */
	targetNodeType: string | null;
	/** Whether the target node is a switch/conditional node */
	isSwitchNode: boolean;
	/** Branch information for switch nodes */
	branches: BranchInfo[];
	/** Whether the target node is a segment node */
	isSegmentNode: boolean;
	/** Whether the segment is the default segment (cannot be deleted) */
	isDefaultSegment: boolean;
	/** Whether the target node is a trigger node (cannot be duplicated or deleted) */
	isTriggerNode: boolean;
	/** Parent switch node ID for segments (used for reordering) */
	parentSwitchNodeId: string | null;
}

/**
 * Initial state for the context menu (closed)
 */
const INITIAL_STATE: ContextMenuState = {
	isOpen: false,
	position: { x: 0, y: 0 },
	targetNodeId: null,
	targetNodeType: null,
	isSwitchNode: false,
	branches: [],
	isSegmentNode: false,
	isDefaultSegment: false,
	isTriggerNode: false,
	parentSwitchNodeId: null,
};

/**
 * Type guard to check if an automation step is a switch step
 */
function isSwitchStep(step: Api.IAutomationStep | undefined): step is Api.ISwitchAutomationStep {
	if (!step) {
		return false;
	}
	return step._type === 'SwitchAutomationStep';
}

/**
 * Extracts branch information from a switch node's automation step data
 */
function extractBranchInfo(automationStep: Api.ISwitchAutomationStep): BranchInfo[] {
	const cases = automationStep.cases ?? [];
	return cases.map((caseStatement, index) => ({
		caseIndex: index,
		caseName: caseStatement.name ?? `Case ${index + 1}`,
		isDefault: caseStatement.isDefault ?? false,
		caseColor: undefined, // Color comes from edge data, not step data
	}));
}

export interface UseContextMenuReturn {
	/** Current context menu state */
	state: ContextMenuState;
	/** Opens the context menu for a specific node */
	openMenu: (event: React.MouseEvent, node: Node<BaseNodeData>) => void;
	/** Closes the context menu */
	closeMenu: () => void;
}

/**
 * Hook for managing context menu state in the automation flow canvas.
 *
 * @example
 * ```typescript
 * const { state, openMenu, closeMenu } = useContextMenu();
 *
 * // In React Flow:
 * <ReactFlow onNodeContextMenu={openMenu} onPaneClick={closeMenu}>
 *   {state.isOpen && <CanvasContextMenu state={state} onClose={closeMenu} />}
 * </ReactFlow>
 * ```
 */
export function useContextMenu(): UseContextMenuReturn {
	const [state, setState] = useState<ContextMenuState>(INITIAL_STATE);

	const openMenu = useCallback((event: React.MouseEvent, node: Node<BaseNodeData>) => {
		// Prevent default browser context menu
		event.preventDefault();

		const nodeData = node.data;

		// Extract node type and check if it's a switch node
		let targetNodeType: string | null = node.type ?? null;
		let isSwitchNode = false;
		let branches: BranchInfo[] = [];
		let isSegmentNode = false;
		let isDefaultSegment = false;
		const isTriggerNode = node.type === 'trigger';

		let parentSwitchNodeId: string | null = null;

		// Check if this is a segment node
		if (node.type === 'segment') {
			isSegmentNode = true;
			const segmentData = nodeData as SegmentNodeData;
			isDefaultSegment = segmentData.isDefault ?? false;
			// Store parent switch node ID for reordering
			parentSwitchNodeId = segmentData.parentSwitchNodeId ?? null;
		} else if (isAutomationStepNodeData(nodeData)) {
			targetNodeType = nodeData.stepType;

			// Check if this is a switch node and extract branch info
			if (isSwitchStep(nodeData.automationStep)) {
				isSwitchNode = true;
				branches = extractBranchInfo(nodeData.automationStep);
			}
		}

		setState({
			isOpen: true,
			position: { x: event.clientX, y: event.clientY },
			targetNodeId: node.id,
			targetNodeType,
			isSwitchNode,
			branches,
			isSegmentNode,
			isDefaultSegment,
			isTriggerNode,
			parentSwitchNodeId,
		});
	}, []);

	const closeMenu = useCallback(() => {
		setState(INITIAL_STATE);
	}, []);

	return {
		state,
		openMenu,
		closeMenu,
	};
}
